import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/services/api";
import { OrderDetails as OrderDetail } from "@/models/orders";
import { OrderStatus } from "@/models/enums";
import { formatDate, formatAmount, checkPermissionsWithAlert } from "@/util";
import { styled } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { useNavigate } from "react-router-dom";
import ServiceDetails from "@/components/orders/serviceDetails";
import ArrowElement from "@/assets/images/ArrowElement.svg";
import { PermissionName } from "@/models/apiModels";
import orderStore from "@/stores/order.store";
import popupService from "@/services/popupService";

const BackToOrdersBtn = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
          }

          &.${buttonUnstyledClasses.root} {
            height: 40px;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 7px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
          }`
);

class OrderDetailsProps {
    backButtonNameKey?: string | null;
}

const OrderDetails = (props: OrderDetailsProps) => {
    const { orderId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [order, setOrder] = useState<OrderDetail | null>(null);

    const handleBackToList = () => {
        navigate("..", { relative: "path" });
    };
    function navigateToEmployee() {
        navigate({
            pathname: `/employees/${order?.employeeId}/info`,
            search: `?returnUrl=${window.location.pathname}`,
        });
    }

    async function startFetching() {
        try {
            const response = await api.getOrder(orderId);
            if (response) {
                setOrder(response.data);
                orderStore.setOrderDocketNumber(response.data.docket);
            }
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    }

    const subsidyAmount = (order: OrderDetail) => {
        return order.currencyCode !== 'NIS' && order.currencyRate > 1 ? (
            <span>
                &nbsp;{formatAmount(order.subsidyAmount, order.currencyId)}
                &nbsp;({formatAmount(order.subsidyAmountInShekel, 1)})
            </span>
        ) : (
            <span>
                &nbsp;{formatAmount(order.subsidyAmountInShekel, 1)}
            </span>
        );
    };

    useEffect(() => {
        if (!checkPermissionsWithAlert(PermissionName.ListOfOrders)) {
            return;
        }

        startFetching();
    }, []);

    if (!order) return null;

    return (
        <>
            <div className="order-header">
                <div>
                    {t("order.headerOrderNumber")}:&nbsp;
                    {order.docket}
                </div>
                <div className="order-header-divider">|</div>
                <div>{order.employeeName}</div>
                <div className="order-header-divider">|</div>
                <span className="white-link-element" onClick={() => navigateToEmployee()}>
                    {order.employeeNumber}
                </span>
            </div>
            <div className="order-details-container">
                <div className="order-general-info">
                    <div className="order-info-title">{t("order.generalInfo")}</div>
                    <div className="order-info-content">
                        <div className="row">
                            <div className="col title">{t("order.general.externalSystemName")}:</div>
                            <div className="col value">{order.externalSystem}</div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col title">{t("order.general.orderStatus")}:</div>
                            <div className="col value">
                                <div
                                    className={
                                        order.status.toString() === OrderStatus[OrderStatus.OK]
                                            ? "status-color-active"
                                            : "status-color-inactive"
                                    }
                                ></div>
                                <div>&nbsp;{order.status}</div>
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col title">{t("order.general.orderType")}:</div>
                            <div className="col value">
                                <div style={{ fontWeight: "700" }}>&nbsp;{t(`enums.OrderType.${order.type}`)}</div>
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col title">{t("order.general.orderCreationDate")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatDate(order.createdAt)}</div>
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col title">{t("order.general.arrivalDate")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatDate(order.arrivalDate)}</div>
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col title">{t("order.general.departureDate")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatDate(order.departureDate)}</div>
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col title">{t("order.general.orderTotal")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatAmount(order.total, order.currencyId)}</div>
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col title">{t("order.general.subsidizedAmount")}:</div>
                            <div className="col value">
                                {subsidyAmount(order)}
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col header">{t("order.general.employee")}</div>
                            <div className="col value">
                                <div></div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col title">{t("order.general.amountToPay")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatAmount(order.total - order.subsidyAmount, order.currencyId)}</div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col title">{t("order.general.amountPaid")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatAmount(order.paidByEmployee, order.currencyId)}</div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col title">{t("order.general.amountHasLeft")}:</div>
                            <div className="col value">
                                <div>
                                    &nbsp;
                                    {formatAmount(
                                        order.total - order.subsidyAmount - order.paidByEmployee,
                                        order.currencyId
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="divider"></div>
                        <div className="row">
                            <div className="col header">{t("order.general.organization")}</div>
                            <div className="col value">
                                <div></div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col title">{t("order.general.amountToPay")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatAmount(order.subsidyAmount, order.currencyId)}</div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col title">{t("order.general.amountPaid")}:</div>
                            <div className="col value">
                                <div>&nbsp;{formatAmount(order.paidByWallet, order.currencyId)}</div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col title">{t("order.general.amountHasLeft")}:</div>
                            <div className="col value">
                                <div>
                                    &nbsp;{formatAmount(order.subsidyAmount - order.paidByWallet, order.currencyId)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="order-services-container">
                    <div className="order-services-title">
                        <div>{t("order.services")}</div>
                        <BackToOrdersBtn className="basic-button" onClick={() => handleBackToList()}>
                            <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                            {props.backButtonNameKey ? t(props.backButtonNameKey) : t("general.Back")}
                        </BackToOrdersBtn>
                    </div>
                    {order.services
                        .sort((a, b) => a.serviceNumber - b.serviceNumber)
                        .map((service) => (
                            <ServiceDetails service={service} key={service.serviceBaseId}></ServiceDetails>
                        ))}
                </div>
            </div>
        </>
    );
};

export default OrderDetails;
